// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@werner/App/Components/XngCarousel";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    slides = "slides",
}

export type CardModel = {
    fields: {
        cardType: string;
        title: string;
        content: string;
        image: string;
        imageAlignment: string;
        video: string;
        enableBorderTop: boolean;
        url: LinkFieldValue;
        openInNewTab: boolean;
        hasBorderTop: boolean;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: CardModel[];
    };
}

type Props = OwnProps;

interface TwoColumnsCarouselStyles {
    container?: InjectableCss;
}

const twoColumnsCarouselStyles: TwoColumnsCarouselStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const TwoColumnsCarousel: React.FC<Props> = ({ fields: { slides }, id }) => {
    const styles = twoColumnsCarouselStyles;

    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel>
                {slides.map(slide => {
                    const component =
                        slide.fields.cardType === "Video"
                            ? createWidgetElement("Video/Video", {
                                  fields: {
                                      src: slide.fields.video,
                                      autoPlay: false,
                                      addContent: slide.fields.content?.length > 0,
                                      content: slide.fields.content,
                                      contentId: id,
                                  },
                              })
                            : createWidgetElement("BrandDetails/BrandDetailsInfoCard", {
                                  fields: {
                                      title: slide.fields.title,
                                      direction: "horizontal",
                                      content: slide.fields.content || "",
                                      image: slide.fields.image,
                                      imageAlignment: slide.fields.imageAlignment, 
                                      url: slide.fields.url,
                                      hasBorderTop: slide.fields.hasBorderTop,
                                      openInNewTab: slide.fields.openInNewTab,
                                      contentId: id,
                                  },
                              });
                    return <React.Fragment key={slide.fields.title}>{component}</React.Fragment>;
                })}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TwoColumnsCarousel,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Two Columns Carousel",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.slides,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { title } }) => title || "Slide",
                defaultValue: [],
                isRequired: true,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "cardType",
                        editorTemplate: "RadioButtonsField",
                        displayName: "Card Type",
                        options: [
                            {
                                displayName: "Info Card",
                                value: "InfoCard",
                            },
                            {
                                displayName: "Video",
                                value: "Video",
                            },
                        ],
                        defaultValue: "InfoCard",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "content",
                        displayName: "Content",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.cardType === "InfoCard",
                    },
                    {
                        name: "imageAlignment",
                        displayName: "Image Alignment ",
                        editorTemplate: "RadioButtonsField",
                        options: [
                            {
                                displayName: "Left",
                                value: "flex-start",
                            },
                            {
                                displayName: "Center",
                                value: "center",
                            },
                            {
                                displayName: "Right",
                                value: "flex-end",
                            },
                        ],
                        defaultValue: "center",
                        isVisible: widget => widget.fields.cardType === "InfoCard",
                    },
                    {
                        name: "video",
                        displayName: "Video Src",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.cardType === "Video",
                    },
                    {
                        name: "url",
                        displayName: "Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open In a new tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        variant: "toggle",
                    },
                    {
                        name: "hasBorderTop",
                        displayName: "Has a border on top",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        variant: "toggle",
                        isVisible: widget => widget.fields.cardType === "InfoCard",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
